import axios, { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { FieldValues, Path, UseFormSetError } from 'react-hook-form';

import { Option } from '@/components/Forms/Dropdown';
import { Alerts } from '@/hooks/useAlerts';
import { ValidationErrorResponse } from '@/types/resources';

import { countryList } from './data';
import { recipientTypes } from './data/payments';

export const getInitialsFromString = (name: string) => {
  return name
    .split(' ')
    .map((str) => str[0].toUpperCase())
    .filter((_, i) => i < 2)
    .join('');
};

export const handleFormErrors = <T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  alerts?: Alerts,
  ignoreSplit = false,
  showErrorsInModal?: boolean,
) => {
  if (axios.isAxiosError(error)) {
    const err = error as AxiosError<ValidationErrorResponse<T>>;
    if (!err.response?.data?.errors) {
      return alerts?.error();
    }
    (Object.keys(err.response.data.errors) as Array<keyof T>).forEach((key) => {
      const fieldKey = ignoreSplit
        ? (key as string)
        : (key as string).split('.')[0];
      setError(fieldKey as unknown as Path<T>, {
        type: 'server',
        message: err.response!.data.errors[key][0],
      });
    });
    if (showErrorsInModal) {
      const errors = (
        Object.keys(err.response.data.errors) as Array<keyof T>
      ).map((key) => {
        return err.response!.data.errors[key][0];
      });
      return alerts?.error('Oops!', `${errors}`);
    }
    return alerts?.error(
      'Oops!',
      "We couldn't submit your form yet. Please fix the incorrect fields above.",
    );
  }
  return alerts?.error();
};

export const numFormatter = (num: number, precision = 1, decimal?: number) => {
  if (num > 999 && num < 1000000) {
    return (num / 1000).toFixed(precision) + 'K'; // convert to K for number from > 1000 < 1 million
  } else if (num >= 1000000000) {
    return (num / 1000000000).toFixed(precision) + 'B'; // convert to B for number from > 1 billion
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(precision) + 'M'; // convert to M for number from > 1 million
  } else if (num <= 999) {
    return decimal ? num.toFixed(decimal) : num; // if value < 1000, nothing to do
  }
};

export const ucFirst = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const download = (
  data: string,
  filename: string,
  type = 'application/octetstream',
  useBom = true,
) => {
  const BOM = '\uFEFF';
  const fileData = useBom ? BOM + data : data;
  const blob = new Blob([fileData], { type });
  const url = window.URL || window.webkitURL;
  const href = url.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('download', filename);
  a.setAttribute('href', href);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const downloadZip = (blob: Blob, filename: string) => {
  const url = window.URL || window.webkitURL;
  const href = url.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('download', filename);
  a.setAttribute('href', href);
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const splitMatchedText = (str: string, keywords: string) => {
  const regExp = new RegExp(keywords, 'gi');
  return str.replace(regExp, (match) => `<span>${match}</span>`);
};

export const humanFileSize = (bytes: number, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

  let u = -1;

  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return bytes.toFixed(dp) + ' ' + units[u];
};

export const durationFormatter = (durationInSeconds: number) => {
  const minutes = Math.floor(durationInSeconds / 60);
  const seconds = durationInSeconds - minutes * 60;

  return `${minutes < 10 ? '0' : ''}${minutes}:${
    seconds < 10 ? '0' : ''
  }${seconds}`;
};

export const isObject = (variable: any) => {
  return (
    typeof variable === 'object' &&
    !Array.isArray(variable) &&
    variable !== null
  );
};

export const ensureStringContainsPositiveDigitsOnly = (str: string) => {
  if (str === '') {
    return true;
  }

  return /^\d*[0-9]\d*$/.test(str);
};

export const ensureStringContainsDecimalNumbersOnly = (str: string) => {
  if (str === '') {
    return true;
  }

  return (
    /^\d+\.\d{0,2}$/.test(str) || ensureStringContainsPositiveDigitsOnly(str)
  );
};

export const stringsArrayToDropdownOptions = (
  arr: string[],
): Option<string>[] => arr.map((value) => ({ label: value, value }));

export const removeExtension = (filename: string) => {
  return filename.substring(0, filename.lastIndexOf('.')) || filename;
};

export const renderCountryName = (countryCode: string) => {
  return countryList?.find((country) => country['alpha-2'] === countryCode)
    ?.name;
};

export const renderRecipientTypeName = (type: string) => {
  return recipientTypes?.find((recipientType) => type === recipientType.value)
    ?.label;
};

export const renderCurrencySign = (currency: string) => {
  switch (currency) {
    case 'euro':
      return '€';
    case 'dollar':
      return '$';
    case 'pound':
      return '£';
    default:
      return '€';
  }
};

export const renderInvoiceAmount = (amount: number, gpm?: boolean) => {
  if (gpm) {
    return (0.85 * amount).toFixed(2);
  }
  return amount;
};

const currencyCodeSigns: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
};

/** Sign for an ISO currency code (EUR/USD/GBP), falling back to the code. */
export const renderCurrencyCodeSign = (currency: string) =>
  currencyCodeSigns[currency] ?? currency;

/** Sign + amount rounded to cents, e.g. "€120.5" (avoids float sums like 91.75999999999999). */
export const formatInvoiceAmount = (amount: number, currency: string) =>
  `${renderCurrencyCodeSign(currency)}${parseFloat(amount.toFixed(2))}`;

export function base64ToBlob(base64Data: string) {
  const parts = base64Data.split(';base64,');
  const imageType = parts[0].split(':')[1];
  const base64 = parts[1];
  const binaryString = window.atob(base64);
  const length = binaryString.length;
  const bytes = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: imageType });
}

export const checkIfWindows = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('windows')) {
    return true;
  }
  return false;
};

export function formatNumberWithCommas(number: number) {
  if (typeof number !== 'number' || isNaN(number)) {
    return '-';
  }

  if (number % 1 === 0) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  } else {
    let formattedNumber = number.toFixed(2);
    return formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

export const calculateAge = (birthday: string) => {
  const now = DateTime.now();
  const birthDate = DateTime.fromISO(birthday);
  const age = now.diff(birthDate, 'years');
  return Math.floor(age.years);
};

export const replaceQuotes = (text: string) => {
  let isInsideQuotes = false; // Track whether we are inside quotes

  return text.replace(/'/g, (match, offset, string) => {
    // Handle contractions and possessives (e.g., I'm, John's)
    if (/\b\w+'\w/.test(string.slice(offset - 1, offset + 2))) {
      return '’';
    }

    // Handle opening and closing quotes properly
    if (
      !isInsideQuotes &&
      // eslint-disable-next-line no-useless-escape
      (offset === 0 || string[offset - 1].match(/[\s(\[{"—]/))
    ) {
      isInsideQuotes = true;
      return '‘'; // Opening single quote
    } else {
      isInsideQuotes = false;
      return '’'; // Closing single quote
    }
  });
};
